import { useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/ui/user-avatar';
import type { DailyTaskComment } from '@/features/daily-tasks/services/daily-tasks.service';
import { CommentContent } from '@/features/daily-tasks/components/CommentContent';
import { CommentViewsIndicator, CommentMentionsIndicator } from '@/features/daily-tasks/components/CommentReadStatus';

interface CommentCardProps {
  comment: DailyTaskComment;
  currentUserId?: string;
  scrollRootRef?: HTMLElement | null;
  onViewed: (commentId: string) => void;
}

export function CommentCard({
  comment,
  currentUserId,
  scrollRootRef,
  onViewed,
}: CommentCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const hasMarkedViewRef = useRef(false);

  useEffect(() => {
    const element = cardRef.current;
    if (!element || !currentUserId) return;
    if (comment.userId === currentUserId) return;

    const root = scrollRootRef ?? null;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !hasMarkedViewRef.current) {
          hasMarkedViewRef.current = true;
          onViewed(comment.id);
        }
      },
      {
        root,
        threshold: 0.15,
        rootMargin: '0px 0px -8px 0px',
      },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [comment.id, comment.userId, currentUserId, onViewed, scrollRootRef]);

  return (
    <div ref={cardRef} className="space-y-2 rounded-md border p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <UserAvatar
            user={{
              id: comment.user?.id ?? '',
              email: comment.user?.email ?? '',
              fullName: comment.user?.fullName ?? null,
              avatarUrl: comment.user?.avatarUrl ?? null,
            }}
            size="md"
          />
          <div>
            <div className="text-sm font-medium">
              {comment.user?.fullName ?? comment.user?.email ?? 'Usuário'}
            </div>
            <div className="text-xs text-muted-foreground">
              {format(new Date(comment.createdAt), 'dd/MM/yyyy, HH:mm', {
                locale: ptBR,
              })}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center">
          <CommentViewsIndicator comment={comment} />
          <Button variant="ghost" size="sm" type="button" aria-label="Mais opções">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="whitespace-pre-wrap text-sm">
        <CommentContent content={comment.content} />
      </div>

      <CommentMentionsIndicator comment={comment} />
    </div>
  );
}
